import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MonitoringLayout } from "./layout";
import {
  fetchAdfHealthStatus,
  triggerAdfHealthEvaluate,
  acknowledgeAdfAlert,
  type AdfHealthCheckState,
} from "@/lib/api";

const statusStyles: Record<string, { bg: string; text: string; border: string }> = {
  healthy: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  warning: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
  critical: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
};

const checkLabels: Record<string, { name: string; desc: string }> = {
  row_count_parity: { name: "Row Count Parity", desc: "Actual table counts vs ADF watermark" },
  data_freshness: { name: "Data Freshness", desc: "Hours since last successful sync" },
  cdc_gap: { name: "CDC Continuity", desc: "KP_PROTOCOL gap vs watermark" },
  pipeline_failure: { name: "Pipeline Status", desc: "Consecutive pipeline failures" },
};

function CheckCard({ state }: { state: AdfHealthCheckState }) {
  const queryClient = useQueryClient();
  const styles = statusStyles[state.status] ?? statusStyles.healthy;
  const label = checkLabels[state.checkType] ?? { name: state.checkType, desc: "" };

  const ackMutation = useMutation({
    mutationFn: () => acknowledgeAdfAlert(state.checkType),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["adfHealthStatus"] }),
  });

  return (
    <div className={`rounded-lg border p-4 ${styles.bg} ${styles.border}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className={`font-medium ${styles.text}`}>{label.name}</div>
          <div className="text-xs text-gray-500">{label.desc}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles.bg} ${styles.text} border ${styles.border}`}>
          {state.status}
        </span>
      </div>

      {state.lastAlertSummary && state.status !== "healthy" && (
        <div className="text-xs text-gray-600 mt-2 bg-white/50 rounded p-2">
          {state.lastAlertSummary}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
        <span>
          {state.lastCheckedAt
            ? `Checked: ${new Date(state.lastCheckedAt).toLocaleTimeString()}`
            : "Never checked"}
        </span>
        {state.consecutiveFailures > 0 && (
          <button
            onClick={() => ackMutation.mutate()}
            disabled={ackMutation.isPending}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Acknowledge
          </button>
        )}
      </div>
    </div>
  );
}

function RunHistoryTable({ runs }: { runs: any[] }) {
  if (!runs.length) return <div className="text-sm text-gray-400 p-4">No runs recorded yet</div>;

  return (
    <div className="overflow-auto max-h-80">
      <table className="text-xs" style={{ minWidth: "900px" }}>
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="text-left p-2 font-medium text-gray-500 whitespace-nowrap">Time</th>
            <th className="text-left p-2 font-medium text-gray-500 whitespace-nowrap">Check</th>
            <th className="text-left p-2 font-medium text-gray-500 whitespace-nowrap">Status</th>
            <th className="text-left p-2 font-medium text-gray-500">Summary</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run, i) => {
            const styles = statusStyles[run.Status] ?? statusStyles.healthy;
            return (
              <tr key={run.RunId || i} className="border-t">
                <td className="p-2 text-gray-500 whitespace-nowrap">
                  {run.CheckedAt ? new Date(run.CheckedAt).toLocaleString() : "—"}
                </td>
                <td className="p-2 whitespace-nowrap">{checkLabels[run.CheckType]?.name ?? run.CheckType}</td>
                <td className="p-2 whitespace-nowrap">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${styles.bg} ${styles.text}`}>
                    {run.Status}
                  </span>
                </td>
                <td className="p-2 text-gray-600">{run.Summary}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdfHealthPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["adfHealthStatus"],
    queryFn: fetchAdfHealthStatus,
    refetchInterval: 30000,
  });

  const evaluateMutation = useMutation({
    mutationFn: triggerAdfHealthEvaluate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["adfHealthStatus"] }),
  });

  return (
    <MonitoringLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">ADF Health Monitor</h1>
            <p className="text-sm text-gray-500 mt-1">
              Automated health checks for Azure Data Factory pipelines (every 5 minutes)
            </p>
          </div>
          <button
            onClick={() => evaluateMutation.mutate()}
            disabled={evaluateMutation.isPending}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {evaluateMutation.isPending ? "Evaluating..." : "Run Now"}
          </button>
        </div>

        {isLoading && <div className="text-gray-500">Loading health status...</div>}
        {error && <div className="text-red-500">Failed to load health status</div>}

        {data && (
          <>
            {/* Overall status banner */}
            {data.overallStatus !== "healthy" && (
              <div className={`rounded-lg p-3 ${statusStyles[data.overallStatus].bg} ${statusStyles[data.overallStatus].border} border`}>
                <span className={`font-medium ${statusStyles[data.overallStatus].text}`}>
                  Overall: {data.overallStatus.toUpperCase()}
                </span>
              </div>
            )}

            {/* Check cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.states.map(state => (
                <CheckCard key={state.checkType} state={state} />
              ))}
            </div>

            {/* Recent runs */}
            <div className="bg-white border rounded-lg">
              <div className="p-3 border-b">
                <h3 className="text-sm font-semibold text-gray-900">Recent Runs</h3>
              </div>
              <RunHistoryTable runs={data.recentRuns} />
            </div>
          </>
        )}
      </div>
    </MonitoringLayout>
  );
}
